import React, { useEffect, useRef, useState } from 'react';
import {
  Maximize,
  Minimize,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { formatVideoTime } from '../../services/videoTime';
import {
  clampTrimEnd,
  clampTrimStart,
  isFullTrimRange,
  isTrimRangeValid,
  isTrimTimeWithinRange,
} from '../../services/videoTrim';

interface GeneratedVideoPlayerProps {
  src: string;
  duration: number;
  trimStart: number;
  trimEnd: number;
  isSilent: boolean;
  disabled?: boolean;
  onDurationChange: (duration: number) => void;
  onTrimChange: (start: number, end: number) => void;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const GeneratedVideoPlayer: React.FC<GeneratedVideoPlayerProps> = ({
  src,
  duration,
  trimStart,
  trimEnd,
  isSilent,
  disabled = false,
  onDurationChange,
  onTrimChange,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTrimPreviewing, setIsTrimPreviewing] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const safeDuration = duration > 0 ? duration : 0;
  const start = safeDuration > 0 ? clamp(trimStart, 0, safeDuration) : 0;
  const end = safeDuration > 0 ? clamp(trimEnd, 0, safeDuration) : 0;
  const selectedDuration = Math.max(0, end - start);
  const trimRangeValid = isTrimRangeValid(start, end, safeDuration);
  const isFullRange = isFullTrimRange(start, end, safeDuration);
  const canSetStart = !disabled && safeDuration > 0 && currentTime < end - 0.05;
  const canSetEnd = !disabled && safeDuration > 0 && currentTime > start + 0.05;

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    const video = videoRef.current;
    video?.pause();
    setCurrentTime(0);
    setIsPlaying(false);
    setIsTrimPreviewing(false);
  }, [src]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const enter = () => setIsFullscreen(true);
    const exit = () => setIsFullscreen(false);
    video.addEventListener('webkitbeginfullscreen', enter);
    video.addEventListener('webkitendfullscreen', exit);
    return () => {
      video.removeEventListener('webkitbeginfullscreen', enter);
      video.removeEventListener('webkitendfullscreen', exit);
    };
  }, []);

  const stopTrimPreview = () => {
    videoRef.current?.pause();
    setIsTrimPreviewing(false);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      const restartAtStart =
        video.currentTime < start ||
        video.currentTime >= Math.max(start, end - 0.05);
      if (safeDuration > 0 && restartAtStart) {
        video.currentTime = start;
        setCurrentTime(start);
      }
      setIsTrimPreviewing(false);
      void video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
      setIsTrimPreviewing(false);
    }
  };

  const handleSeek = (value: number) => {
    const video = videoRef.current;
    if (!video) return;
    const next = safeDuration > 0 ? clamp(value, 0, safeDuration) : 0;
    if (isTrimPreviewing) {
      video.pause();
      setIsTrimPreviewing(false);
    } else if (
      !video.paused &&
      safeDuration > 0 &&
      !isTrimTimeWithinRange(next, start, end)
    ) {
      video.pause();
    }
    video.currentTime = next;
    setCurrentTime(next);
  };

  const handleTrimStartChange = (value: number) => {
    if (disabled || safeDuration <= 0) return;
    const nextStart = clampTrimStart(value, end);
    stopTrimPreview();
    onTrimChange(nextStart, end);
    if (videoRef.current) {
      videoRef.current.currentTime = nextStart;
      setCurrentTime(nextStart);
    }
  };

  const handleTrimEndChange = (value: number) => {
    if (disabled || safeDuration <= 0) return;
    const nextEnd = clampTrimEnd(value, start, safeDuration);
    stopTrimPreview();
    onTrimChange(start, nextEnd);
    if (videoRef.current && videoRef.current.currentTime > nextEnd) {
      videoRef.current.currentTime = nextEnd;
      setCurrentTime(nextEnd);
    }
  };

  const playSelection = () => {
    const video = videoRef.current;
    if (!video || !trimRangeValid) return;
    if (isTrimPreviewing) {
      stopTrimPreview();
      return;
    }
    video.pause();
    video.currentTime = start;
    setCurrentTime(start);
    setIsTrimPreviewing(true);
    void video.play().catch(() => setIsTrimPreviewing(false));
  };

  const setStartAtPlayhead = () => {
    if (!canSetStart) return;
    const nextStart = clampTrimStart(currentTime, end);
    onTrimChange(nextStart, end);
    setCurrentTime(nextStart);
  };

  const setEndAtPlayhead = () => {
    if (!canSetEnd) return;
    const nextEnd = clampTrimEnd(currentTime, start, safeDuration);
    onTrimChange(start, nextEnd);
    setCurrentTime(nextEnd);
  };

  const useFullClip = () => {
    if (safeDuration <= 0) return;
    stopTrimPreview();
    onTrimChange(0, safeDuration);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };

  const handleTimeUpdate = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    setCurrentTime(video.currentTime);
    if (video.seeking) return;
    if (safeDuration > 0 && end > 0 && video.currentTime >= end) {
      video.pause();
      video.currentTime = end;
      setCurrentTime(end);
      setIsTrimPreviewing(false);
    }
  };

  const handleLoadedMetadata = (
    event: React.SyntheticEvent<HTMLVideoElement>
  ) => {
    const nextDuration = event.currentTarget.duration;
    if (Number.isFinite(nextDuration) && nextDuration > 0) {
      onDurationChange(nextDuration);
      setCurrentTime(0);
    }
  };

  const toggleMute = () => {
    if (isSilent) return;
    if (isMuted || volume === 0) {
      if (volume === 0) setVolume(1);
      setIsMuted(false);
    } else {
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (value: number) => {
    const next = clamp(value, 0, 1);
    setVolume(next);
    if (next > 0) {
      setIsMuted(false);
    } else {
      setIsMuted(true);
    }
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }
    const shell = shellRef.current;
    if (shell?.requestFullscreen) {
      void shell.requestFullscreen().catch(() => undefined);
      return;
    }
    const video = videoRef.current as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | null;
    video?.webkitEnterFullscreen?.();
  };

  const startPercent = safeDuration > 0 ? (start / safeDuration) * 100 : 0;
  const endPercent = safeDuration > 0 ? (end / safeDuration) * 100 : 100;

  return (
    <div ref={shellRef} className="character-video-player-shell">
      <video
        ref={videoRef}
        className="character-video-player"
        src={src}
        controls={false}
        muted={isSilent || isMuted}
        playsInline
        preload="metadata"
        aria-label="Generated video preview"
        onClick={togglePlay}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setIsTrimPreviewing(false);
        }}
      />

      <div className="character-video-player-controls character-video-trim-console">
        <div className="character-video-player-transport">
          <button
            type="button"
            className="character-video-player-control"
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
          </button>

          <span className="character-video-player-time">
            {formatVideoTime(currentTime)} / {formatVideoTime(safeDuration)}
          </span>

          {!isSilent && (
            <div className="character-video-volume">
              <button
                type="button"
                className="character-video-player-control"
                onClick={toggleMute}
                aria-label={isMuted ? 'Unmute video' : 'Mute video'}
              >
                {isMuted || volume === 0 ? (
                  <VolumeX size={18} />
                ) : (
                  <Volume2 size={18} />
                )}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={isMuted ? 0 : volume}
                onChange={(event) =>
                  handleVolumeChange(Number(event.target.value))
                }
                aria-label="Volume"
              />
            </div>
          )}

          <button
            type="button"
            className="character-video-player-control character-video-fullscreen"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>

        {safeDuration > 0 && (
          <>
            <div className="character-video-trim-strip">
              <span className="character-video-trim-code">
                {formatVideoTime(start)}
              </span>
              <div
                className="character-video-trim-timeline"
                title="Drag the handles to trim. Click the timeline to seek."
              >
                <div className="character-video-trim-track" />
                <div
                  className="character-video-trim-range"
                  style={{
                    left: `${startPercent}%`,
                    right: `${100 - endPercent}%`,
                  }}
                />
                <input
                  type="range"
                  className="character-video-seek-input"
                  min={0}
                  max={safeDuration}
                  step={0.01}
                  value={Math.max(0, Math.min(currentTime, safeDuration))}
                  onChange={(event) => handleSeek(Number(event.target.value))}
                  aria-label="Seek video"
                  aria-valuetext={formatVideoTime(currentTime)}
                />
                <input
                  type="range"
                  className="character-video-trim-input character-video-trim-input-start"
                  min={0}
                  max={safeDuration}
                  step={0.1}
                  value={start}
                  onChange={(event) =>
                    handleTrimStartChange(Number(event.target.value))
                  }
                  disabled={disabled}
                  aria-label="Trim start"
                  aria-valuetext={formatVideoTime(start)}
                />
                <input
                  type="range"
                  className="character-video-trim-input character-video-trim-input-end"
                  min={0}
                  max={safeDuration}
                  step={0.1}
                  value={end}
                  onChange={(event) =>
                    handleTrimEndChange(Number(event.target.value))
                  }
                  disabled={disabled}
                  aria-label="Trim end"
                  aria-valuetext={formatVideoTime(end)}
                />
              </div>
              <span className="character-video-trim-code">
                {formatVideoTime(end)}
              </span>
            </div>

            <div className="character-video-trim-foot">
              <span className="character-video-trim-selected">
                {formatVideoTime(selectedDuration)}
              </span>
              <span className="character-video-trim-selected-label">
                selected
              </span>
              <div className="character-video-trim-text-actions">
                <button
                  type="button"
                  className="character-video-trim-action primary"
                  onClick={playSelection}
                  disabled={!trimRangeValid}
                >
                  {isTrimPreviewing ? <Pause size={16} /> : <Play size={16} />}
                  {isTrimPreviewing ? 'Stop' : 'Play selection'}
                </button>
                <button
                  type="button"
                  className="character-video-trim-action"
                  onClick={setStartAtPlayhead}
                  disabled={!canSetStart}
                >
                  Set start
                </button>
                <button
                  type="button"
                  className="character-video-trim-action"
                  onClick={setEndAtPlayhead}
                  disabled={!canSetEnd}
                >
                  Set end
                </button>
                <button
                  type="button"
                  className="character-video-trim-action secondary"
                  onClick={useFullClip}
                  disabled={disabled || isFullRange}
                >
                  Use full clip
                </button>
              </div>
            </div>

            {isSilent && (
              <span className="character-video-silent-note">
                Silent preview · audio removed on save
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GeneratedVideoPlayer;
